"use client";

import { c as _c } from "react/compiler-runtime";
import { useCompactMode } from "@/contexts/CompactModeContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiUpdateDropRequest } from "@/generated/models/ApiUpdateDropRequest";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropUpdateMutation } from "@/hooks/drops/useDropUpdateMutation";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import { selectEditingDropId, setEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { DropInteractionParams } from "./Drop";
import { DropLocation } from "./Drop";
import type { BoostAnimationState } from "./DropBoostAnimation";
import DropBoostAnimation from "./DropBoostAnimation";
import WaveDropActions from "./WaveDropActions";
import WaveDropAuthorPfp from "./WaveDropAuthorPfp";
import WaveDropContent from "./WaveDropContent";
import WaveDropHeader from "./WaveDropHeader";
import WaveDropMetadata from "./WaveDropMetadata";
import WaveDropMobileMenu from "./WaveDropMobileMenu";
import WaveDropRatings from "./WaveDropRatings";
import WaveDropReactions from "./WaveDropReactions";
import WaveDropReply from "./WaveDropReply";
enum GroupingThreshold {
  TIME_DIFFERENCE = 60000,
}
const shouldGroupWithDrop = (currentDrop: ExtendedDrop, otherDrop: ExtendedDrop | null): boolean => {
  if (!otherDrop || currentDrop.parts.length > 1) {
    return false;
  }
  const isSameAuthor = currentDrop.author.handle === otherDrop.author.handle;
  const isWithinTimeThreshold = Math.abs(currentDrop.created_at - otherDrop.created_at) <= GroupingThreshold.TIME_DIFFERENCE;
  if (!isSameAuthor || !isWithinTimeThreshold) {
    return false;
  }
  const bothNotReplies = !currentDrop.reply_to && !otherDrop.reply_to;
  const repliesInSameThread = currentDrop.reply_to?.drop_id === otherDrop.reply_to?.drop_id;
  return bothNotReplies || repliesInSameThread;
};
const RANK_STYLES = {
  1: "tw-border-l tw-border-[#E8D48A]/40 tw-border-y-0 tw-border-r-0",
  2: "tw-border-l tw-border-[#DDDDDD]/40 tw-border-y-0 tw-border-r-0",
  3: "tw-border-l tw-border-[#CD7F32]/40 tw-border-y-0 tw-border-r-0",
  default: "tw-border-l tw-border-iron-600/40 tw-border-y-0 tw-border-r-0"
} as const;
const getColorClasses = ({
  isActiveDrop,
  rank,
  isDrop,
  location
}: {
  isActiveDrop: boolean;
  rank: number | null;
  isDrop: boolean;
  location: DropLocation;
}): string => {
  if (isActiveDrop) {
    return "tw-bg-[#3CCB7F]/10 tw-border-l tw-border-l-[#3CCB7F] tw-border-solid tw-border-y-0 tw-border-r-0";
  }
  if (!isDrop) {
    const isWaveView = location === DropLocation.WAVE;
    const hoverClass = isWaveView ? "desktop-hover:hover:tw-bg-iron-800/50" : "";
    const ringClasses = isWaveView ? "" : "tw-ring-1 tw-ring-inset tw-ring-iron-800";
    const bgClass = isWaveView ? "" : "tw-bg-iron-950";
    return `${bgClass} ${ringClasses} ${hoverClass}`.trim();
  }
  const rankClass = RANK_STYLES[rank as keyof typeof RANK_STYLES];
  return `${rankClass} tw-transition-shadow tw-duration-300`.trim();
};
const getDropClasses = (isActiveDrop: boolean, groupingClass: string, location: DropLocation, rank: number | null, isDrop: boolean): string => {
  const baseClasses = "touch-select-none tw-cursor-default tw-relative tw-group tw-w-full tw-flex tw-flex-col tw-px-4 tw-transition-colors tw-duration-300";
  const streamClasses = "tw-rounded-xl";
  const chatDropClasses = isDrop ? "tw-rounded-lg tw-my-0.5" : "";
  const rankClasses = getColorClasses({
    isActiveDrop,
    rank,
    isDrop,
    location
  });
  const locationClasses = location === DropLocation.MY_STREAM || location === DropLocation.PROFILE ? streamClasses : chatDropClasses;
  return `${baseClasses} ${groupingClass} ${locationClasses} ${rankClasses}`.trim();
};
interface WaveDropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: ExtendedDrop | null;
  readonly nextDrop: ExtendedDrop | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuote: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
const WaveDrop = t0 => {
  const $ = _c(140);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onQuote,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote
  } = t0;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [boostAnimation, setBoostAnimation] = useState(null);
  const dropRef = useRef(null);
  const dispatch = useDispatch();
  const editingDropId = useSelector(selectEditingDropId);
  const isEditing = editingDropId === drop.id;
  const longPressTimeoutRef = useRef(null);
  const touchStartPosition = useRef(null);
  const dropUpdateMutation = useDropUpdateMutation();
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isDrop = drop.drop_type === ApiDropType.Participatory;
  let t1;
  if ($[0] !== drop || $[1] !== isDrop || $[2] !== previousDrop) {
    t1 = !isDrop && shouldGroupWithDrop(drop, previousDrop);
    $[0] = drop;
    $[1] = isDrop;
    $[2] = previousDrop;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const shouldGroupWithPreviousDrop = t1;
  let t2;
  if ($[4] !== drop || $[5] !== isDrop || $[6] !== nextDrop) {
    t2 = !isDrop && shouldGroupWithDrop(drop, nextDrop);
    $[4] = drop;
    $[5] = isDrop;
    $[6] = nextDrop;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const shouldGroupWithNextDrop = t2;
  const isMobile = useIsMobileDevice();
  const compact = useCompactMode();
  const isProfileView = location === DropLocation.PROFILE;
  const showAuthorInfo = !shouldGroupWithPreviousDrop || isProfileView;
  let t3;
  if ($[8] !== isProfileView || $[9] !== shouldGroupWithNextDrop || $[10] !== shouldGroupWithPreviousDrop) {
    t3 = () => {
      if (isProfileView) {
        return "tw-py-4";
      }
      if (shouldGroupWithPreviousDrop) {
        return "tw-pt-1";
      }
      if (shouldGroupWithNextDrop) {
        return "tw-pt-4 tw-pb-1";
      }
      return "tw-py-4";
    };
    $[8] = isProfileView;
    $[9] = shouldGroupWithNextDrop;
    $[10] = shouldGroupWithPreviousDrop;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const getGroupingClass = t3;
  const groupingClass = getGroupingClass();
  let t4;
  if ($[12] !== dispatch || $[13] !== editingDropId || $[14] !== isMobile) {
    t4 = () => {
      if (!isMobile) {
        return;
      }
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[12] = dispatch;
    $[13] = editingDropId;
    $[14] = isMobile;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleLongPress = t4;
  let t5;
  if ($[16] !== handleLongPress || $[17] !== isEditing || $[18] !== isMobile) {
    t5 = e => {
      if (!isMobile) {
        return;
      }
      if (isEditing) {
        return;
      }
      const touch = e.touches[0];
      touchStartPosition.current = {
        x: touch.clientX,
        y: touch.clientY
      };
      longPressTimeoutRef.current = setTimeout(handleLongPress, 500);
    };
    $[16] = handleLongPress;
    $[17] = isEditing;
    $[18] = isMobile;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleTouchStart = t5;
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
      touchStartPosition.current = null;
    };
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleTouchEnd = t6;
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e_0 => {
      if (!touchStartPosition.current) {
        return;
      }
      const touch_0 = e_0.touches[0];
      const deltaX = Math.abs(touch_0.clientX - touchStartPosition.current.x);
      const deltaY = Math.abs(touch_0.clientY - touchStartPosition.current.y);
      if ((deltaX > 10 || deltaY > 10) && longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const handleTouchMove = t7;
  let t8;
  if ($[22] !== activePartIndex || $[23] !== dispatch || $[24] !== drop || $[25] !== editingDropId || $[26] !== onReply) {
    t8 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex].part_id
      });
    };
    $[22] = activePartIndex;
    $[23] = dispatch;
    $[24] = drop;
    $[25] = editingDropId;
    $[26] = onReply;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const handleOnReply = t8;
  let t9;
  if ($[28] !== activePartIndex || $[29] !== dispatch || $[30] !== drop || $[31] !== editingDropId || $[32] !== onQuote) {
    t9 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
      onQuote({
        drop,
        partId: drop.parts[activePartIndex].part_id
      });
    };
    $[28] = activePartIndex;
    $[29] = dispatch;
    $[30] = drop;
    $[31] = editingDropId;
    $[32] = onQuote;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const handleOnQuote = t9;
  let t10;
  if ($[34] !== dispatch || $[35] !== editingDropId) {
    t10 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
    };
    $[34] = dispatch;
    $[35] = editingDropId;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const handleOnAddReaction = t10;
  let t11;
  if ($[37] !== dispatch || $[38] !== drop.id) {
    t11 = () => {
      setIsSlideUp(false);
      dispatch(setEditingDropId(drop.id));
    };
    $[37] = dispatch;
    $[38] = drop.id;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  const handleOnEdit = t11;
  let t12;
  if ($[40] !== activePartIndex || $[41] !== dispatch || $[42] !== drop || $[43] !== dropUpdateMutation) {
    t12 = (newContent, mentions) => {
      const cleanedMentions = (mentions ?? drop.mentioned_users).map(_temp);
      const updateRequest = {
        parts: drop.parts.map((part, index) => ({
          content: index === activePartIndex ? newContent : part.content,
          quoted_drop: part.quoted_drop ?? null,
          media: part.media
        })),
        title: drop.title,
        metadata: drop.metadata,
        referenced_nfts: drop.referenced_nfts,
        mentioned_users: cleanedMentions,
        signature: null
      };
      dispatch(setEditingDropId(null));
      dropUpdateMutation.mutate({
        dropId: drop.id,
        request: updateRequest,
        currentDrop: drop
      });
    };
    $[40] = activePartIndex;
    $[41] = dispatch;
    $[42] = drop;
    $[43] = dropUpdateMutation;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  const handleEditSave = t12;
  let t13;
  if ($[45] !== dispatch) {
    t13 = () => {
      dispatch(setEditingDropId(null));
    };
    $[45] = dispatch;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const handleEditCancel = t13;
  let t14;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setBoostAnimation(null);
    };
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const handleBoostAnimationComplete = t14;
  let t15;
  if ($[48] !== drop.context_profile_context?.boosted || $[49] !== drop.id) {
    t15 = () => {
      if (!dropRef.current) {
        return;
      }
      const rect = dropRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const isBoosted = drop.context_profile_context?.boosted ?? false;
      setBoostAnimation({
        id: `${drop.id}-${Date.now()}`,
        x,
        y,
        type: isBoosted ? "unboost" : "boost"
      });
    };
    $[48] = drop.context_profile_context?.boosted;
    $[49] = drop.id;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  drop.context_profile_context?.boosted;
  const handleMobileBoostAnimation = t15;
  let t16;
  let t17;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
    t17 = [];
    $[51] = t16;
    $[52] = t17;
  } else {
    t16 = $[51];
    t17 = $[52];
  }
  useEffect(t16, t17);
  const effectiveIsSlideUp = isSlideUp && !isEditing;
  const dropClasses = getDropClasses(isActiveDrop, groupingClass, location, drop.rank, isDrop);
  const t18 = `${isDrop && location === DropLocation.WAVE ? "tw-px-4 tw-py-0.5" : ""} ${isProfileView ? "tw-mb-3" : ""} tw-w-full`;
  let t19;
  if ($[53] !== drop.author || $[54] !== drop.reply_to || $[55] !== drop.wave || $[56] !== dropViewDropId || $[57] !== onReplyClick || $[58] !== previousDrop) {
    t19 = drop.reply_to && (drop.reply_to.drop_id !== previousDrop?.reply_to?.drop_id || drop.author.handle !== previousDrop.author.handle) && drop.reply_to.drop_id !== dropViewDropId && <WaveDropReply onReplyClick={onReplyClick} dropId={drop.reply_to.drop_id} dropPartId={drop.reply_to.drop_part_id} maybeDrop={drop.reply_to.drop ? {
      ...drop.reply_to.drop,
      wave: drop.wave
    } : null} />;
    $[53] = drop.author;
    $[54] = drop.reply_to;
    $[55] = drop.wave;
    $[56] = dropViewDropId;
    $[57] = onReplyClick;
    $[58] = previousDrop;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== drop || $[61] !== showAuthorInfo) {
    t20 = showAuthorInfo && <WaveDropAuthorPfp drop={drop} />;
    $[60] = drop;
    $[61] = showAuthorInfo;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  const t21 = showAuthorInfo ? "calc(100% - 3.5rem)" : "100%";
  let t22;
  if ($[63] !== t21) {
    t22 = {
      maxWidth: t21
    };
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== activePartIndex || $[66] !== drop || $[67] !== isStorm || $[68] !== showAuthorInfo || $[69] !== showWaveInfo) {
    t23 = showAuthorInfo && <WaveDropHeader drop={drop} showWaveInfo={showWaveInfo} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} />;
    $[65] = activePartIndex;
    $[66] = drop;
    $[67] = isStorm;
    $[68] = showAuthorInfo;
    $[69] = showWaveInfo;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  const t24 = `tw-w-full ${shouldGroupWithPreviousDrop && !isProfileView ? "tw-ml-[3.25rem]" : ""}`;
  let t25;
  if ($[71] !== activePartIndex || $[72] !== drop || $[73] !== dropUpdateMutation.isPending || $[74] !== handleEditCancel || $[75] !== handleEditSave || $[76] !== handleLongPress || $[77] !== isEditing || $[78] !== onDropContentClick || $[79] !== onQuoteClick) {
    t25 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isEditing={isEditing} isSaving={dropUpdateMutation.isPending} onSave={handleEditSave} onCancel={handleEditCancel} />;
    $[71] = activePartIndex;
    $[72] = drop;
    $[73] = dropUpdateMutation.isPending;
    $[74] = handleEditCancel;
    $[75] = handleEditSave;
    $[76] = handleLongPress;
    $[77] = isEditing;
    $[78] = onDropContentClick;
    $[79] = onQuoteClick;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  let t26;
  if ($[81] !== t24 || $[82] !== t25) {
    t26 = <div className={t24}>{t25}</div>;
    $[81] = t24;
    $[82] = t25;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  let t27;
  if ($[84] !== t22 || $[85] !== t23 || $[86] !== t26) {
    t27 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-1" style={t22}>{t23}{t26}</div>;
    $[84] = t22;
    $[85] = t23;
    $[86] = t26;
    $[87] = t27;
  } else {
    t27 = $[87];
  }
  let t28;
  if ($[88] !== t20 || $[89] !== t27) {
    t28 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t20}{t27}</div>;
    $[88] = t20;
    $[89] = t27;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] !== activePartIndex || $[92] !== drop || $[93] !== handleOnEdit || $[94] !== handleOnQuote || $[95] !== handleOnReply || $[96] !== isEditing || $[97] !== isMobile || $[98] !== showReplyAndQuote) {
    t29 = !isMobile && showReplyAndQuote && !isEditing && <WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} onQuote={handleOnQuote} onEdit={handleOnEdit} />;
    $[91] = activePartIndex;
    $[92] = drop;
    $[93] = handleOnEdit;
    $[94] = handleOnQuote;
    $[95] = handleOnReply;
    $[96] = isEditing;
    $[97] = isMobile;
    $[98] = showReplyAndQuote;
    $[99] = t29;
  } else {
    t29 = $[99];
  }
  const t30 = `tw-mx-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 ${compact ? "tw-ml-11 tw-w-[calc(100%-2.5rem)]" : "tw-ml-[3.25rem] tw-w-[calc(100%-3.25rem)]"}`;
  let t31;
  if ($[100] !== drop.metadata) {
    t31 = drop.metadata.length > 0 && <WaveDropMetadata metadata={drop.metadata} />;
    $[100] = drop.metadata;
    $[101] = t31;
  } else {
    t31 = $[101];
  }
  let t32;
  if ($[102] !== drop) {
    t32 = !!drop.raters_count && <WaveDropRatings drop={drop} />;
    $[102] = drop;
    $[103] = t32;
  } else {
    t32 = $[103];
  }
  let t33;
  if ($[104] !== drop) {
    t33 = <WaveDropReactions drop={drop} />;
    $[104] = drop;
    $[105] = t33;
  } else {
    t33 = $[105];
  }
  let t34;
  if ($[106] !== t30 || $[107] !== t31 || $[108] !== t32 || $[109] !== t33) {
    t34 = <div className={t30}>{t31}{t32}{t33}</div>;
    $[106] = t30;
    $[107] = t31;
    $[108] = t32;
    $[109] = t33;
    $[110] = t34;
  } else {
    t34 = $[110];
  }
  let t35;
  if ($[111] !== drop || $[112] !== effectiveIsSlideUp || $[113] !== handleMobileBoostAnimation || $[114] !== handleOnAddReaction || $[115] !== handleOnEdit || $[116] !== handleOnQuote || $[117] !== handleOnReply || $[118] !== longPressTriggered || $[119] !== showReplyAndQuote) {
    t35 = <WaveDropMobileMenu drop={drop} isOpen={effectiveIsSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onQuote={handleOnQuote} onAddReaction={handleOnAddReaction} onEdit={handleOnEdit} onBoostAnimation={handleMobileBoostAnimation} />;
    $[111] = drop;
    $[112] = effectiveIsSlideUp;
    $[113] = handleMobileBoostAnimation;
    $[114] = handleOnAddReaction;
    $[115] = handleOnEdit;
    $[116] = handleOnQuote;
    $[117] = handleOnReply;
    $[118] = longPressTriggered;
    $[119] = showReplyAndQuote;
    $[120] = t35;
  } else {
    t35 = $[120];
  }
  let t36;
  if ($[121] !== boostAnimation || $[122] !== handleBoostAnimationComplete) {
    t36 = <DropBoostAnimation animation={boostAnimation} onComplete={handleBoostAnimationComplete} />;
    $[121] = boostAnimation;
    $[122] = handleBoostAnimationComplete;
    $[123] = t36;
  } else {
    t36 = $[123];
  }
  let t37;
  if ($[124] !== drop.serial_no || $[125] !== drop.stableHash || $[126] !== dropClasses || $[127] !== handleTouchEnd || $[128] !== handleTouchMove || $[129] !== handleTouchStart || $[130] !== t19 || $[131] !== t28 || $[132] !== t29 || $[133] !== t34 || $[134] !== t35 || $[135] !== t36) {
    t37 = <div ref={dropRef} className={dropClasses} data-wave-drop-id={drop.stableHash} data-serial-no={drop.serial_no} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchMove}>{t19}{t28}{t29}{t34}{t35}{t36}</div>;
    $[124] = drop.serial_no;
    $[125] = drop.stableHash;
    $[126] = dropClasses;
    $[127] = handleTouchEnd;
    $[128] = handleTouchMove;
    $[129] = handleTouchStart;
    $[130] = t19;
    $[131] = t28;
    $[132] = t29;
    $[133] = t34;
    $[134] = t35;
    $[135] = t36;
    $[136] = t37;
  } else {
    t37 = $[136];
  }
  let t38;
  if ($[137] !== t18 || $[138] !== t37) {
    t38 = <div className={t18}>{t37}</div>;
    $[137] = t18;
    $[138] = t37;
    $[139] = t38;
  } else {
    t38 = $[139];
  }
  return t38;
};
export default memo(WaveDrop);
function _temp(user) {
  return {
    mentioned_profile_id: user.mentioned_profile_id,
    handle_in_content: user.handle_in_content
  };
}